/**
 * The live quiz clock. A run is `started_at` plus per-question seconds; every
 * phone and the stadium screen derive the current phase from the SERVER clock
 * (skew-corrected, see clockSample), so nobody presses Next and a missed push
 * changes nothing. The SQL twin is public.quiz_phase (migration
 * 20261028100000_live_quiz.sql); the boundary table in quizClock.test.ts and
 * in scripts/db/rls-smoke.sql must stay identical.
 *
 * Per question: GET_READY (the question is on every phone before the speed
 * clock starts) → ANSWERING (answer_seconds) → REVEAL (reveal_seconds). The
 * answer key is released at revealAt = end of answering + GRACE, not when the
 * reveal phase starts: late answers (network) are still accepted in the grace.
 */
export const QUIZ_GET_READY_SECONDS = 3;
export const QUIZ_GRACE_MS = 1500;

export type QuizPhaseName = 'waiting' | 'get_ready' | 'answering' | 'reveal' | 'finished' | 'cancelled';

export interface QuizTiming {
  status: 'draft' | 'running' | 'finished' | 'cancelled';
  started_at: string | null;
  intro_seconds: number;
  answer_seconds: number;
  reveal_seconds: number;
  question_count: number;
}

export interface QuizPhase {
  phase: QuizPhaseName;
  /** 0-based; null while waiting, finished or cancelled. */
  questionIndex: number | null;
  /** When this phase ends (ms since epoch); null when nothing is scheduled. */
  endsAt: number | null;
  /** When answering opens for this question. */
  opensAt: number | null;
  /** When the answer key may be shown: end of answering + grace. */
  revealAt: number | null;
}

const periodMs = (q: QuizTiming) => (QUIZ_GET_READY_SECONDS + q.answer_seconds + q.reveal_seconds) * 1000;

export function quizPhase(q: QuizTiming, nowMs: number): QuizPhase {
  const none = { questionIndex: null, endsAt: null, opensAt: null, revealAt: null };
  if (q.status === 'cancelled') return { phase: 'cancelled', ...none };
  if (!q.started_at) return { phase: 'waiting', ...none };
  const start = Date.parse(q.started_at);
  const introEnd = start + q.intro_seconds * 1000;
  if (nowMs < introEnd) return { phase: 'waiting', ...none, endsAt: introEnd };
  const P = periodMs(q);
  const k = Math.floor((nowMs - introEnd) / P);
  if (k >= q.question_count) return { phase: 'finished', ...none };
  const base = introEnd + k * P;
  const opensAt = base + QUIZ_GET_READY_SECONDS * 1000;
  const closesAt = opensAt + q.answer_seconds * 1000;
  const revealAt = closesAt + QUIZ_GRACE_MS;
  if (nowMs < opensAt) return { phase: 'get_ready', questionIndex: k, endsAt: opensAt, opensAt, revealAt };
  if (nowMs < closesAt) return { phase: 'answering', questionIndex: k, endsAt: closesAt, opensAt, revealAt };
  return { phase: 'reveal', questionIndex: k, endsAt: base + P, opensAt, revealAt };
}

/** When the whole run is over (ms since epoch); null before it starts. */
export function quizEndsAt(q: QuizTiming): number | null {
  if (!q.started_at) return null;
  return Date.parse(q.started_at) + q.intro_seconds * 1000 + q.question_count * periodMs(q);
}

/** Kahoot-like: wrong = 0; correct = 500 + 500 × (1 − elapsed/answer time), rounded, clamped to the window. */
export function quizPoints(correct: boolean, elapsedMs: number, answerSeconds: number): number {
  if (!correct) return 0;
  const limit = answerSeconds * 1000;
  const e = Math.min(Math.max(elapsedMs, 0), limit);
  return Math.round(500 + 500 * (1 - e / limit));
}

export interface ClockSample {
  /** server clock − phone clock, ms: server time ≈ Date.now() + offsetMs */
  offsetMs: number;
  rttMs: number;
}

/** NTP-style estimate from one request: offset = server now − midpoint(sent, received). */
export function clockSample(sentAtMs: number, receivedAtMs: number, serverNowIso: string): ClockSample {
  const rttMs = receivedAtMs - sentAtMs;
  const midpoint = sentAtMs + rttMs / 2;
  return { offsetMs: Math.round(Date.parse(serverNowIso) - midpoint), rttMs };
}

/** The sample with the lowest round trip is the most accurate one; keep it. */
export function betterSample(current: ClockSample | null, next: ClockSample): ClockSample {
  if (!current) return next;
  return next.rttMs < current.rttMs ? next : current;
}

/** Fallback fetches at a phase boundary are spread over 300–2500 ms so 2000 phones never fetch at once. */
export function boundaryJitterMs(random: () => number = Math.random): number {
  return 300 + Math.floor(random() * 2200);
}
